import React, { useState, useMemo } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput, ActivityIndicator, Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OverlayFooter, OverlayHeader, OverlayPanel } from '../OverlayPanel';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';

/** Generic full-screen sort/filter picker — extracted from what was
 * originally ContactSortFilterModal (Contacts-only). All logic there was
 * already generic (fields/filters/values arrive entirely as props); the
 * only Contacts-specific parts were the hardcoded translated strings, now
 * pulled out into the `labels` prop (with plain-English defaults) so any
 * module — Products, Vendors, Catalog, Orders, Visits, and Contacts itself
 * — can reuse this one component instead of maintaining a near-duplicate
 * per module. */

/** Special sentinel values stored inside a field's normal string[] selection
 * to mean "field has any non-empty value" or "field is missing/blank" —
 * never a real value that could appear in fieldValueOptions, so they can't
 * collide with an actual imported value. */
export { HAS_VALUE_SENTINEL, NO_VALUE_SENTINEL, buildCustomFieldFiltersParam } from './customFieldFilterParam';
import { HAS_VALUE_SENTINEL, NO_VALUE_SENTINEL } from './customFieldFilterParam';

/** A discovered value can be shown differently than it filters by — e.g.
 * Products' vendorIds/catalogueIds checklist filters by a real vendor/
 * category id but should read as that vendor/category's NAME, not a raw id.
 * A plain string means value === label (every other field so far). */
export interface FieldValueOption {
  value: string;
  label: string;
}
type RawFieldValueOption = string | FieldValueOption;
const normalizeOption = (o: RawFieldValueOption): FieldValueOption =>
  typeof o === 'string' ? { value: o, label: o } : o;

export interface SortFilterModalLabels {
  modalTitle: string;
  sortTitle: string;
  filterTitle: string;
  filterHint: string;
  hasValue: string;
  noValueOption: string;
  searchFieldsPlaceholder: string;
  searchValuesPlaceholder: string;
  reset: string;
  anyValue: string;
  selected: string;
  noValues: string;
  noValuesMatchSearch: string;
  noFieldsMatch: string;
  apply: string;
}

const DEFAULT_LABELS: SortFilterModalLabels = {
  modalTitle: 'Sort & Filter',
  sortTitle: 'Sort by',
  filterTitle: 'Filter by',
  filterHint: 'Every selected filter must match (AND), same as combining several at once.',
  hasValue: 'Has a value',
  noValueOption: 'No value',
  searchFieldsPlaceholder: 'Search fields...',
  searchValuesPlaceholder: 'Search values...',
  reset: 'Reset',
  anyValue: 'Any value',
  selected: 'selected',
  noValues: 'No values found',
  noValuesMatchSearch: 'No values match your search',
  noFieldsMatch: 'No fields match your search',
  apply: 'Apply',
};

/** Converts the modal's UI selection state (Record<fieldId, string[]>, with
 * sentinels for has/no-value) into the JSON string the backend's
 * `customFieldFilters` query param expects (CustomFieldFilterEntry[] — see
 * custom-field-filter.util.ts). Returns undefined when there's nothing to
 * filter by, so callers can omit the query param entirely rather than
 * sending `[]`. Shared across every module's list screen — identical logic
 * would otherwise be duplicated per screen. */

const describeSelection = (
  selectedValues: string[],
  labels: SortFilterModalLabels,
  options: RawFieldValueOption[] = []
): string => {
  if (selectedValues.includes(HAS_VALUE_SENTINEL)) return labels.hasValue;
  if (selectedValues.includes(NO_VALUE_SENTINEL)) return labels.noValueOption;
  const labelByValue = new Map(options.map(normalizeOption).map((o) => [o.value, o.label]));
  return selectedValues.map((v) => labelByValue.get(v) || v).join(', ');
};

export interface SortField {
  id: string;
  label: string;
  /** false for sort-only fields (dates) or fields whose filter UI lives
   * elsewhere — those still show up under "Sort by" but never render a
   * value checklist here. */
  filterable: boolean;
  /** True for a compound/derived field with no literal value list of its
   * own (e.g. Contacts' "location" — a has-any-of-4-fields check, not one
   * column) — only the "Has a value"/"No value" chips render for it, never
   * the values search/checklist below them. */
  hasValueOnly?: boolean;
}

export interface SortFilterModalProps {
  visible: boolean;
  onClose: () => void;
  sortFields: SortField[];
  sortBy: string | null;
  sortOrder: 'asc' | 'desc';
  onChangeSort: (sortBy: string | null, sortOrder: 'asc' | 'desc') => void;
  customFieldKeys: string[];
  /** Selected filter values, keyed by field id — a fixed filterable
   * field's own id (e.g. "email") or a literal custom field key. */
  customFieldFilters: Record<string, string[]>;
  onChangeCustomFieldFilters: (filters: Record<string, string[]>) => void;
  /** Seen-values cache, keyed the same way as customFieldFilters. Each
   * entry is either a plain string (value === label) or a {value,label}
   * pair when the filtered-by value and its display text differ (e.g. a
   * real vendor/category id filtered by, shown as that record's name). */
  fieldValueOptions: Record<string, RawFieldValueOption[]>;
  loadingFieldValues: string | null;
  /** isFixedField tells the caller which endpoint to hit — fixed filterable
   * fields and custom fields are typically fetched via different routes. */
  onExpandField: (fieldId: string, isFixedField: boolean) => void;
  /** Overrides for any of DEFAULT_LABELS' plain-English strings — pass
   * translated values via t() for a localized module (see Contacts' usage),
   * or omit entirely to use the English defaults. */
  labels?: Partial<SortFilterModalLabels>;
  /** Filter only — the screen sorts elsewhere (e.g. Vendors' own sort & filter). */
  hideSort?: boolean;
}

export const SortFilterModal: React.FC<SortFilterModalProps> = ({
  visible,
  onClose,
  sortFields,
  sortBy,
  sortOrder,
  onChangeSort,
  customFieldKeys,
  customFieldFilters,
  onChangeCustomFieldFilters,
  fieldValueOptions,
  loadingFieldValues,
  onExpandField,
  labels: labelsOverride,
  hideSort,
}) => {
  const styles = useThemedStyles(getStyles);
  const insets = useSafeAreaInsets();
  const rtl = useRTLStyles();
  const labels = useMemo(() => ({ ...DEFAULT_LABELS, ...labelsOverride }), [labelsOverride]);
  const [fieldSearch, setFieldSearch] = useState('');
  // Single expanded field across BOTH sections — {fieldId, isFixedField} so
  // onExpandField knows which endpoint to call without re-deriving it.
  const [expanded, setExpanded] = useState<{ fieldId: string; isFixedField: boolean } | null>(null);
  const [valueSearch, setValueSearch] = useState('');

  const activeFilterCount = Object.values(customFieldFilters).filter((v) => v.length > 0).length;

  const filteredSortFields = useMemo(
    () => sortFields.filter((f) => f.label.toLowerCase().includes(fieldSearch.trim().toLowerCase())),
    [sortFields, fieldSearch]
  );
  const filteredCustomFieldKeys = useMemo(
    () => customFieldKeys.filter((k) => k.toLowerCase().includes(fieldSearch.trim().toLowerCase())),
    [customFieldKeys, fieldSearch]
  );

  const toggleFieldValue = (fieldId: string, value: string) => {
    const current = customFieldFilters[fieldId] || [];
    const isSelected = current.includes(value);
    onChangeCustomFieldFilters({
      ...customFieldFilters,
      // Picking a real value always clears any "Has a value"/"No value"
      // selection for this field — the two concepts are mutually exclusive.
      [fieldId]: isSelected ? current.filter((v) => v !== value) : [...current, value].filter((v) => v !== HAS_VALUE_SENTINEL && v !== NO_VALUE_SENTINEL),
    });
  };

  // Toggling "Has a value" / "No value" REPLACES the field's whole
  // selection with just that one sentinel (not additive like real values —
  // both concepts together would be contradictory), or clears it back to
  // empty if the same sentinel is tapped again.
  const toggleHasValueSentinel = (fieldId: string, sentinel: typeof HAS_VALUE_SENTINEL | typeof NO_VALUE_SENTINEL) => {
    const current = customFieldFilters[fieldId] || [];
    const alreadyActive = current.includes(sentinel);
    onChangeCustomFieldFilters({
      ...customFieldFilters,
      [fieldId]: alreadyActive ? [] : [sentinel],
    });
  };

  const clearFieldValues = (fieldId: string) => {
    onChangeCustomFieldFilters({ ...customFieldFilters, [fieldId]: [] });
  };

  const handleExpand = (fieldId: string, isFixedField: boolean) => {
    setValueSearch('');
    if (expanded?.fieldId === fieldId) {
      setExpanded(null);
    } else {
      setExpanded({ fieldId, isFixedField });
      onExpandField(fieldId, isFixedField);
    }
  };

  const handleReset = () => {
    onChangeSort(null, 'asc');
    onChangeCustomFieldFilters({});
  };

  const renderValueChecklist = (fieldId: string, hasValueOnly?: boolean) => {
    const selectedValues = customFieldFilters[fieldId] || [];
    // Defensive: a customColumn-sourced value can arrive as a raw number/
    // boolean (unlike Contacts' bulk-CSV values, always plain text) even if
    // the backend is expected to stringify these before they get here.
    const options = (fieldValueOptions[fieldId] || []).map(normalizeOption);
    const filteredOptions = options.filter((o) => o.label.toLowerCase().includes(valueSearch.trim().toLowerCase()));

    // A hasValueOnly field has no literal value list of its own — nothing
    // to fetch/search/select from, only the presence chips below are
    // meaningful, so skip the loading state entirely.
    if (!hasValueOnly && loadingFieldValues === fieldId) {
      return <ActivityIndicator size="small" color={colors.primary} style={{ padding: spacing.md }} />;
    }

    const hasValueActive = selectedValues.includes(HAS_VALUE_SENTINEL);
    const noValueActive = selectedValues.includes(NO_VALUE_SENTINEL);
    const realValueCount = selectedValues.filter((v) => v !== HAS_VALUE_SENTINEL && v !== NO_VALUE_SENTINEL).length;

    return (
      <View style={styles.valueBox}>
        <View style={styles.presenceRow}>
          <TouchableOpacity
            style={[styles.presenceChip, { flexDirection: rtl.rowDirection }, hasValueActive && styles.presenceChipActive]}
            onPress={() => toggleHasValueSentinel(fieldId, HAS_VALUE_SENTINEL)}
          >
            <Text style={[styles.presenceChipText, hasValueActive && styles.presenceChipTextActive]}>{labels.hasValue}</Text>
            {hasValueActive && <Ionicons name="checkmark" size={14} color={colors.white} />}
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.presenceChip, { flexDirection: rtl.rowDirection }, noValueActive && styles.presenceChipActive]}
            onPress={() => toggleHasValueSentinel(fieldId, NO_VALUE_SENTINEL)}
          >
            <Text style={[styles.presenceChipText, noValueActive && styles.presenceChipTextActive]}>{labels.noValueOption}</Text>
            {noValueActive && <Ionicons name="checkmark" size={14} color={colors.white} />}
          </TouchableOpacity>
        </View>

        {!hasValueOnly && (
          <>
            {options.length > 5 && (
              <View style={[styles.valueSearchBar, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="search" size={14} color={colors.text.secondary} />
                <TextInput
                  style={[styles.valueSearchInput, { textAlign: rtl.textStart }]}
                  placeholder={labels.searchValuesPlaceholder}
                  placeholderTextColor={colors.text.disabled}
                  value={valueSearch}
                  onChangeText={setValueSearch}
                  autoCapitalize="none"
                />
              </View>
            )}
            {realValueCount > 0 && (
              <TouchableOpacity style={styles.clearValuesButton} onPress={() => clearFieldValues(fieldId)}>
                <Text style={styles.clearValuesButtonText}>{labels.anyValue} ({realValueCount} {labels.selected})</Text>
              </TouchableOpacity>
            )}
            <ScrollView style={styles.valueScrollList} keyboardShouldPersistTaps="handled" nestedScrollEnabled>
              {options.length === 0 ? (
                <Text style={styles.emptyText}>{labels.noValues}</Text>
              ) : filteredOptions.length === 0 ? (
                <Text style={styles.emptyText}>{labels.noValuesMatchSearch}</Text>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <TouchableOpacity
                      key={option.value}
                      style={[styles.valueItem, { flexDirection: rtl.rowDirection }, isSelected && styles.valueItemActive]}
                      onPress={() => toggleFieldValue(fieldId, option.value)}
                    >
                      <Text style={[styles.valueItemText, isSelected && styles.valueItemTextActive]} numberOfLines={1}>{option.label}</Text>
                      {isSelected && <Ionicons name="checkmark" size={16} color={colors.white} />}
                    </TouchableOpacity>
                  );
                })
              )}
            </ScrollView>
          </>
        )}
      </View>
    );
  };

  return (
    <OverlayPanel visible={visible} onClose={onClose}>
      {/* Full-screen page inside a transparent Modal — React Native adds no
          safe-area padding, so keep the header clear of the notch/status
          bar and the footer clear of the home indicator. */}
      <View style={[styles.page, { paddingTop: insets.top, paddingBottom: insets.bottom }]}>
        <OverlayHeader icon="swap-vertical-outline" title={labels.modalTitle} onClose={onClose} right={(
          <TouchableOpacity
            onPress={handleReset}
            disabled={!sortBy && activeFilterCount === 0}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Text style={[styles.resetText, (!sortBy && activeFilterCount === 0) && styles.resetTextDisabled]}>
              {labels.reset}
            </Text>
          </TouchableOpacity>
        )} />

        <View style={[styles.searchBar, { flexDirection: rtl.rowDirection }]}>
          <Ionicons name="search" size={16} color={colors.text.secondary} />
          <TextInput
            style={[styles.searchInput, { textAlign: rtl.textStart }]}
            placeholder={labels.searchFieldsPlaceholder}
            placeholderTextColor={colors.text.disabled}
            value={fieldSearch}
            onChangeText={setFieldSearch}
            autoCapitalize="none"
          />
          {fieldSearch.length > 0 && (
            <TouchableOpacity onPress={() => setFieldSearch('')}>
              <Ionicons name="close-circle" size={16} color={colors.text.disabled} />
            </TouchableOpacity>
          )}
        </View>

        <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent} keyboardShouldPersistTaps="handled">
          {!hideSort && filteredSortFields.length > 0 && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{labels.sortTitle}</Text>
              <View style={[styles.sortChipsRow, { flexDirection: rtl.rowDirection }]}>
                {filteredSortFields.map((field) => {
                  const isActive = sortBy === field.id;
                  return (
                    <TouchableOpacity
                      key={field.id}
                      style={[styles.sortChip, isActive && styles.sortChipActive]}
                      onPress={() => {
                        if (isActive) {
                          onChangeSort(field.id, sortOrder === 'asc' ? 'desc' : 'asc');
                        } else {
                          onChangeSort(field.id, 'asc');
                        }
                      }}
                    >
                      <Text style={[styles.sortChipText, isActive && styles.sortChipTextActive]}>{field.label}</Text>
                      {isActive && (
                        <Ionicons name={sortOrder === 'asc' ? 'arrow-up' : 'arrow-down'} size={14} color={colors.white} />
                      )}
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>
          )}

          {(filteredSortFields.some((f) => f.filterable) || filteredCustomFieldKeys.length > 0) && (
            <View style={styles.section}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{labels.filterTitle}</Text>
              <Text style={[styles.sectionHint, { textAlign: rtl.textStart }]}>{labels.filterHint}</Text>

              {filteredSortFields.filter((f) => f.filterable).map((field) => {
                const selectedValues = customFieldFilters[field.id] || [];
                const isExpanded = expanded?.fieldId === field.id;
                return (
                  <View key={field.id} style={[styles.fieldBlock, isExpanded && styles.fieldBlockExpanded]}>
                    <TouchableOpacity
                      style={[styles.row, { flexDirection: rtl.rowDirection }, selectedValues.length > 0 && styles.rowActive]}
                      onPress={() => handleExpand(field.id, true)}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.rowText, selectedValues.length > 0 && styles.rowTextActive]} numberOfLines={1}>{field.label}</Text>
                        {selectedValues.length > 0 && (
                          <Text style={[styles.rowSubText, styles.rowTextActive]} numberOfLines={1}>{describeSelection(selectedValues, labels, fieldValueOptions[field.id])}</Text>
                        )}
                      </View>
                      <Ionicons
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={18}
                        color={selectedValues.length > 0 ? colors.white : colors.text.secondary}
                      />
                    </TouchableOpacity>
                    {isExpanded && renderValueChecklist(field.id, field.hasValueOnly)}
                  </View>
                );
              })}

              {filteredCustomFieldKeys.map((key) => {
                const selectedValues = customFieldFilters[key] || [];
                const isExpanded = expanded?.fieldId === key;
                return (
                  <View key={key} style={[styles.fieldBlock, isExpanded && styles.fieldBlockExpanded]}>
                    <TouchableOpacity
                      style={[styles.row, { flexDirection: rtl.rowDirection }, selectedValues.length > 0 && styles.rowActive]}
                      onPress={() => handleExpand(key, false)}
                    >
                      <View style={{ flex: 1 }}>
                        <Text style={[styles.rowText, selectedValues.length > 0 && styles.rowTextActive]} numberOfLines={1}>{key}</Text>
                        {selectedValues.length > 0 && (
                          <Text style={[styles.rowSubText, styles.rowTextActive]} numberOfLines={1}>{describeSelection(selectedValues, labels, fieldValueOptions[key])}</Text>
                        )}
                      </View>
                      <Ionicons
                        name={isExpanded ? 'chevron-up' : 'chevron-down'}
                        size={18}
                        color={selectedValues.length > 0 ? colors.white : colors.text.secondary}
                      />
                    </TouchableOpacity>
                    {isExpanded && renderValueChecklist(key)}
                  </View>
                );
              })}
            </View>
          )}

          {filteredSortFields.length === 0 && filteredCustomFieldKeys.length === 0 && (
            <Text style={styles.emptyText}>{labels.noFieldsMatch}</Text>
          )}
        </ScrollView>

        <OverlayFooter>
          <TouchableOpacity style={styles.applyButton} onPress={onClose}>
            <Text style={styles.applyButtonText}>{labels.apply}</Text>
          </TouchableOpacity>
        </OverlayFooter>
      </View>
    </OverlayPanel>
  );
};

const getStyles = () => StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && { maxWidth: 640, alignSelf: 'center', width: '100%', backgroundColor: colors.background.surfaceRaised }),
  },
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerTitle: {
    flex: 1,
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.text.primary,
  },
  resetText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  resetTextDisabled: {
    color: colors.text.disabled,
  },
  searchBar: {
    alignItems: 'center',
    gap: spacing.sm,
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    padding: 0,
  },
  body: {
    flex: 1,
    marginTop: spacing.md,
  },
  bodyContent: {
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.xl,
  },
  section: {
    marginBottom: spacing.lg,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '700',
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.sm,
  },
  sectionHint: {
    fontSize: typography.fontSize.xs,
    color: colors.text.disabled,
    marginBottom: spacing.sm,
    marginTop: -spacing.xs,
  },
  sortChipsRow: {
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  sortChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: 999,
    backgroundColor: colors.background.surfaceRaised,
    borderWidth: 1,
    borderColor: colors.border,
  },
  sortChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  sortChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontWeight: '500',
  },
  sortChipTextActive: {
    color: colors.white,
  },
  fieldBlock: {
    marginBottom: spacing.sm,
    borderRadius: spacing.borderRadius.md,
  },
  // A stronger visual boundary around whichever field is currently
  // expanded, so an expanded checklist doesn't read as bleeding into
  // neighboring rows.
  fieldBlockExpanded: {
    borderWidth: 1,
    borderColor: colors.primary,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 2px 8px rgba(0,0,0,0.08)' } as any
      : {
          shadowColor: colors.literal?.black ?? '#000',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.1,
          shadowRadius: 6,
          elevation: 3,
        }),
  },
  row: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surfaceRaised,
  },
  rowActive: {
    backgroundColor: colors.primary,
  },
  rowText: {
    fontSize: typography.fontSize.base,
    color: colors.text.primary,
    fontWeight: '500',
  },
  rowSubText: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: 2,
  },
  rowTextActive: {
    color: colors.white,
  },
  valueBox: {
    marginTop: spacing.xs,
    padding: spacing.xs,
  },
  presenceRow: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  presenceChip: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  presenceChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  presenceChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    fontWeight: '600',
  },
  presenceChipTextActive: {
    color: colors.white,
  },
  valueSearchBar: {
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    marginBottom: spacing.xs,
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
  },
  valueSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    padding: 0,
  },
  clearValuesButton: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  clearValuesButtonText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: '600',
  },
  valueScrollList: {
    maxHeight: 240,
  },
  valueItem: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.sm,
  },
  valueItemActive: {
    backgroundColor: colors.primary,
  },
  valueItemText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.primary,
    flex: 1,
  },
  valueItemTextActive: {
    color: colors.white,
    fontWeight: '600',
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.disabled,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },
  footer: {
    padding: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  applyButton: {
    backgroundColor: colors.primary,
    borderRadius: spacing.borderRadius.md,
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
  applyButtonText: {
    color: colors.white,
    fontSize: typography.fontSize.base,
    fontWeight: '700',
  },
});
